import axios from 'axios';
import { trackApiCall } from './apiMetricsService';

/**
 * Exponential backoff retry mechanism
 * Retries failed requests with delays: 2s, 4s, 8s (3 total attempts)
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum retry attempts (default: 3)
 * @param {object} trackingInfo - Information for metrics tracking
 * @returns {Promise} - Result of the function
 */
async function retryWithExponentialBackoff(fn, maxRetries = 3, trackingInfo = {}) {
  const delays = [2000, 4000, 8000]; // 2s, 4s, 8s
  let lastError;
  const startTime = Date.now();
  let retryCount = 0;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await fn();
      const responseTime = Date.now() - startTime;
      
      // Track successful call
      if (trackingInfo?.provider) {
        await trackApiCall({
          provider: trackingInfo?.provider,
          endpoint: trackingInfo?.endpoint || 'unknown',
          responseTimeMs: responseTime,
          retryCount: retryCount,
          success: true,
          statusCode: 200,
          metadata: trackingInfo?.metadata || {}
        });
      }
      
      return result;
    } catch (error) {
      lastError = error;
      retryCount++;
      const isLastAttempt = attempt === maxRetries - 1;
      
      // Check if error is retryable (network errors, 5xx errors, timeouts)
      const isRetryable = 
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ETIMEDOUT' ||
        error?.response?.status >= 500 ||
        error?.response?.status === 429 || // Rate limit
        !error?.response; // Network error

      if (!isRetryable || isLastAttempt) {
        const responseTime = Date.now() - startTime;
        
        console.error(`AI API call failed after ${attempt + 1} attempt(s):`, {
          error: error?.message,
          status: error?.response?.status,
          attempt: attempt + 1
        });
        
        // Track failed call
        if (trackingInfo?.provider) {
          await trackApiCall({
            provider: trackingInfo?.provider,
            endpoint: trackingInfo?.endpoint || 'unknown',
            responseTimeMs: responseTime,
            retryCount: retryCount,
            success: false,
            errorType: error?.code || (error?.response?.status >= 500 ? 'server_error' : 'unknown'),
            errorMessage: error?.message,
            statusCode: error?.response?.status || 0,
            metadata: { ...trackingInfo?.metadata, maxRetriesReached: isLastAttempt }
          });
        }
        
        throw error;
      }

      const delay = delays?.[attempt];
      console.warn(`AI API call failed (attempt ${attempt + 1}/${maxRetries}). Retrying in ${delay}ms...`, {
        error: error?.message,
        status: error?.response?.status,
        nextRetryIn: `${delay}ms`
      });

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

export async function callLambdaFunction(endpoint, payload, trackingInfo = {}) {
  return retryWithExponentialBackoff(async () => {
    try {
      const response = await axios?.post(endpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 60000, // 60 second timeout
      });
      return response?.data;
    } catch (error) {
      const errorDetails = {
        message: error?.response?.data?.error || error?.message,
        status: error?.response?.status,
        details: error?.response?.data?.details
      };
      console.error('Lambda Function Error:', { error, details: errorDetails });
      throw new Error(errorDetails.message || 'Lambda function call failed');
    }
  }, 3, trackingInfo);
}

export { retryWithExponentialBackoff };